import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { Country } from '../../../models/common/country.model';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { CrmProspect, CrmRoleOption } from '../../crm.model';
import { CrmService } from '../../crm.service';

@Component({
  selector: 'app-prospect-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    SelectButtonModule,
    BreadcrumbModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './prospect-form.component.html',
  styleUrls: ['./prospect-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProspectFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private baseMasterDataService = inject(BaseMasterDataService);
  private sessionStorageService = inject(SessionStorageService);
  private messageService = inject(MessageService);

  public loading = signal(false);
  public saving = signal(false);
  public submitted = signal(false);
  public prospectId = signal<number | null>(null);
  public countries = signal<Country[]>([]);
  public crmRoles = signal<CrmRoleOption[]>([]);
  public homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  public breadcrumbItems = signal<MenuItem[]>([]);

  public statuses = [
    { label: 'New', value: 'NEW' },
    { label: 'Qualified', value: 'QUALIFIED' },
    { label: 'Proposal', value: 'PROPOSAL' },
    { label: 'Lost', value: 'LOST' }
  ];

  public businessTypes = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' }
  ];

  public prospectQualityOptions = [
    { label: 'High', value: 'HIGH' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Low', value: 'LOW' }
  ];

  public prospectForm = this.fb.group({
    id: [null as number | null],
    sourceLeadId: [null as number | null],
    companyName: ['', Validators.required],
    customerNameLocal: [''],
    pocName: ['', Validators.required],
    pocEmail: ['', [Validators.email]],
    pocPhoneNumber: [''],
    mobileNumber: [''],
    status: ['NEW'],
    source: [''],
    prospectQuality: ['MEDIUM'],
    businessType: ['B2B' as 'B2B' | 'B2C', Validators.required],
    roles: [['CUSTOMER'] as string[]],
    natureOfBusiness: [''],
    taxRegNumber: [''],
    crNumber: [null as number | null],
    street1: [''],
    street1Local: [''],
    city: [''],
    cityLocal: [''],
    postalCode: [''],
    countryId: [null as number | null],
    websiteURL: [''],
    companyId: [null as number | null],
    kamCustomerId: [null as number | null]
  }, { validators: [this.contactMethodValidator] });

  ngOnInit() {
    this.loadCountries();
    this.loadRoles();
    this.prospectForm.get('businessType')?.valueChanges.subscribe(() => this.syncBusinessTypeValidators());
    this.syncBusinessTypeValidators();

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.prospectId.set(id);
      this.setBreadcrumb('Edit Prospect');
      this.loadProspect(id);
      return;
    }

    this.setBreadcrumb('New Prospect');
    this.prospectForm.patchValue({
      status: 'NEW',
      prospectQuality: 'MEDIUM',
      countryId: this.toolbarService.customer()?.company?.countryId ?? null,
      companyId: this.toolbarService.customer()?.company?.id ?? null
    });
  }

  loadCountries() {
    const cachedCountries = this.sessionStorageService.getCountryList() ?? [];
    if (cachedCountries.length) {
      this.countries.set(cachedCountries);
      this.restoreCountrySelection();
    }

    this.baseMasterDataService.getCountries('en-US').subscribe({
      next: countries => {
        if (countries?.length) {
          this.countries.set(countries);
          this.restoreCountrySelection();
        }
      },
      error: () => {
        if (!this.countries().length) {
          this.messageService.add({ severity: 'warn', summary: 'Countries', detail: 'Unable to load country list.' });
        }
      }
    });
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadProspect(id: number) {
    this.loading.set(true);
    this.crmService.getProspect(id).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => {
        if (response.success && response.data) {
          this.prospectForm.patchValue({
            ...response.data,
            countryId: this.numericId(response.data.countryId)
          });
          this.restoreCountrySelection();
          this.setBreadcrumb(response.data.prospectNumber || 'Edit Prospect');
          this.syncBusinessTypeValidators();
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Prospects', detail: 'Unable to load prospect.' })
    });
  }

  saveProspect() {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    this.syncBusinessTypeValidators();

    if (this.prospectForm.invalid) {
      this.prospectForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }

    const prospectData = this.prospectForm.value as CrmProspect;
    this.saving.set(true);

    if (prospectData.id) {
      prospectData.updatedByCustomerId = this.toolbarService.customer()?.id ?? prospectData.updatedByCustomerId;
      this.crmService.updateProspect(prospectData).pipe(finalize(() => this.saving.set(false))).subscribe({
        next: response => {
          if (response.success) {
            this.messageService.add({ severity: 'success', summary: 'Prospect Updated', detail: 'Prospect details saved.' });
            this.router.navigate(['/crm/prospects', response.data?.id ?? prospectData.id]);
          }
        }
      });
      return;
    }

    prospectData.companyId = this.toolbarService.customer()?.company?.id ?? prospectData.companyId;
    prospectData.createdByCustomerId = this.toolbarService.customer()?.id ?? prospectData.createdByCustomerId;
    this.crmService.addProspect(prospectData).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: response => {
        if (response.success) {
          this.messageService.add({ severity: 'success', summary: 'Prospect Created', detail: 'Prospect has been added.' });
          this.router.navigate(['/crm/prospects', response.data?.id]);
        }
      }
    });
  }

  cancel() {
    const id = this.prospectForm.get('id')?.value;
    this.router.navigate(id ? ['/crm/prospects', id] : ['/crm/prospects']);
  }

  syncBusinessTypeValidators() {
    ['taxRegNumber', 'crNumber', 'postalCode'].forEach(controlName => {
      const control = this.prospectForm.get(controlName);
      if (!control) return;
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  hasError(controlName: string): boolean {
    const control = this.prospectForm.get(controlName);
    return !!control && control.invalid && (this.submitted() || control.touched || control.dirty);
  }

  hasContactMethodError(): boolean {
    const email = this.prospectForm.get('pocEmail');
    const phone = this.prospectForm.get('pocPhoneNumber');
    const mobile = this.prospectForm.get('mobileNumber');
    return this.prospectForm.hasError('contactMethod') &&
      (this.submitted() || !!email?.touched || !!phone?.touched || !!mobile?.touched || !!email?.dirty || !!phone?.dirty || !!mobile?.dirty);
  }

  private contactMethodValidator(control: AbstractControl): ValidationErrors | null {
    const email = String(control.get('pocEmail')?.value || '').trim();
    const phone = String(control.get('pocPhoneNumber')?.value || '').trim();
    const mobile = String(control.get('mobileNumber')?.value || '').trim();
    return email || phone || mobile ? null : { contactMethod: true };
  }

  private restoreCountrySelection() {
    const countryId = this.numericId(this.prospectForm.get('countryId')?.value);
    if (!countryId) return;
    const selectedCountry = this.countries().find(country => Number(country.id) === countryId);
    this.prospectForm.patchValue({ countryId: selectedCountry?.id ?? countryId }, { emitEvent: false });
  }

  private numericId(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') return null;
    const id = Number(value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private setBreadcrumb(currentLabel: string) {
    this.breadcrumbItems.set([
      { label: 'CRM', routerLink: '/crm/prospects' },
      { label: 'Prospects', routerLink: '/crm/prospects' },
      { label: currentLabel }
    ]);
  }
}
